import { execAndWaitForAll } from "../../Helpers/Functions/promise"
import { selectUserName } from "../../Helpers/Functions/Selectors/auth"
import { APIAppWorker } from "../../Models/API/AppWorker"
import { AppWorkerRole } from "../../Models/API/AppWorkerRole"
import { APIWorker } from "../../Models/API/Worker"
import { hasMore } from "../../Models/Client/RemoteData"
import { SortExpression } from "../../Models/Client/SortExpression"
import { MainThunkDispatch, State } from "../../Reducers"
import { apiEndpoints } from "../../Routes/api"
import { failMessage, successMessage } from "../Client/MessagesActions"
import { apiFetch } from "../Client/NetworkActions"
import { WorkerType } from "../Types"

export const createHumanWorker =
    (orgUsername: string, emails: string[]) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.createWorker({
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername
            })

            await execAndWaitForAll(
                emails.map(email =>
                    dispatch(
                        apiFetch(`${endpoint.url}${email}`, endpoint, {
                            method: "POST",
                            body: JSON.stringify({ email, type: "USER" })
                        })
                    )
                )
            )

            dispatch(successMessage("Worker is created"))
            dispatch(reloadWorkers(orgUsername))
        } catch (error) {
            dispatch(failMessage("Could not create worker", error))
            throw error
        }
    }

export const createBotOrCrowdWorker =
    (orgUsername: string, type: "BOT" | "CROWD") =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.createWorker({
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "POST",
                    body: JSON.stringify({ type })
                })
            )

            dispatch(successMessage("Worker is created"))
            dispatch(reloadWorkers(orgUsername))
        } catch (error) {
            dispatch(failMessage("Could not create worker", error))
            throw error
        }
    }

export const removeWorker =
    (orgUsername: string, id: number) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.removeWorker({
                id,
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "DELETE"
                })
            )

            dispatch(successMessage("Worker is removed"))
            dispatch(reloadWorkers(orgUsername))
        } catch (error) {
            dispatch(failMessage("Could not remove worker", error))
            throw error
        }
    }

export const reloadWorkers =
    (orgUsername: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workers.org[orgUsername]

        if (entry?.list === undefined) {
            return
        }

        await dispatch(listWorkers(orgUsername, 0, entry.list.params))
    }

export const loadMoreWorkers =
    (orgUsername: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workers.org[orgUsername]

        if (entry?.list === undefined || !hasMore(entry)) {
            return
        }

        await dispatch(
            listWorkers(orgUsername, entry.list.pages.length, entry.list.params)
        )
    }

export const listWorkers =
    (orgUsername: string, page: number, sort: SortExpression<APIWorker>) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        try {
            const endpoint = apiEndpoints.getWorkerList({
                org:
                    selectUserName(getState()) === orgUsername
                        ? undefined
                        : orgUsername,
                page,
                pageSize: 30,
                ...sort
            })
            const data = await dispatch(
                apiFetch<{ content: APIWorker[]; total: number }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                return
            }

            dispatch({
                type: WorkerType.setWorkerList,
                meta: {
                    orgUsername,
                    page,
                    sort,
                    total: data.total
                },
                payload: data.content
            })
        } catch (error) {}
    }

export const listAppWorkers =
    (appID: string, page: number, sort: SortExpression<APIAppWorker>) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.getAppWorkerList({
                appID,
                page,
                pageSize: 30,
                ...sort
            })
            const data = await dispatch(
                apiFetch<{ content: APIAppWorker[]; total: number }>(
                    endpoint.url,
                    endpoint
                )
            )

            if (data === undefined) {
                return
            }

            dispatch({
                type: WorkerType.setAppWorkerList,
                meta: {
                    appID,
                    page,
                    sort,
                    total: data.total
                },
                payload: data.content
            })
        } catch (error) {}
    }

export const getAppWorkerByUsername =
    (appID: string, username: string) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.getAppWorkerByUsername({
                appID,
                username
            })
            const data = await dispatch(
                apiFetch<APIAppWorker>(endpoint.url, endpoint)
            )

            if (data === undefined) {
                return
            }

            dispatch({
                type: WorkerType.setAppWorker,
                payload: data
            })
        } catch (error) {}
    }

export const reloadAppWorkers =
    (appID: string) => (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workers.app[appID]

        if (entry?.list === undefined) {
            return
        }

        dispatch(listAppWorkers(appID, 0, entry.list.params))
    }

export const loadMoreAppWorkers =
    (appID: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.workers.app[appID]

        if (entry?.list === undefined || !hasMore(entry)) {
            return
        }

        await dispatch(
            listAppWorkers(appID, entry.list.pages.length, entry.list.params)
        )
    }

export const assignWorkerToApp =
    (appID: string, ids: number[], role: AppWorkerRole) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            await execAndWaitForAll(
                ids.map(id => {
                    const endpoint = apiEndpoints.assignWorkerToApp({
                        appID,
                        id
                    })

                    return dispatch(
                        apiFetch(endpoint.url, endpoint, {
                            method: "POST",
                            body: JSON.stringify({ role })
                        })
                    )
                })
            )

            dispatch(
                successMessage(
                    ids.length > 1
                        ? "Workers are added to project"
                        : "Worker is added to project"
                )
            )
            dispatch(reloadAppWorkers(appID))
        } catch (error) {
            dispatch(failMessage("Could not add worker to project", error))
            throw error
        }
    }

export const unassignWorkerFromApp =
    (appID: string, id: number) => async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.unassignWorkerFromApp({
                id,
                appID
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "DELETE"
                })
            )

            dispatch(successMessage("Worker is removed"))
            dispatch(reloadAppWorkers(appID))
        } catch (error) {
            dispatch(failMessage("Could not remove worker", error))
            throw error
        }
    }

export const updateWorkerInApp =
    (worker: APIAppWorker) => async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.patchWorkerInApp({
                id: worker.worker.id,
                appID: worker.appUuid
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "PATCH",
                    body: JSON.stringify({ role: worker.role })
                })
            )

            dispatch(successMessage("Worker is updated"))
            dispatch(reloadAppWorkers(worker.appUuid))
        } catch (error) {
            dispatch(failMessage("Could not update worker", error))
            throw error
        }
    }
